import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Link,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';

/**
 * PlanVersionEntryAccessDialog — the "who can enter what?" surface for one plan version.
 *
 * Fetches GET /api/finance/planversion/entry-access-analysis/{id}: for every PlanHeader in the
 * version, the (node, function) each principal slot points at and the users those slots resolve to
 * right now — i.e. who will see and be able to enter each header in Plan Entry at login. Setup gaps
 * (unassigned slot, a slot nobody holds, a header nobody can enter) are flagged inline, so a
 * controller can fix the org tree before the entry round starts.
 */

interface AccessUser {
  userId: string;
  userName?: string | null;
}

interface PrincipalSlot {
  type: string;
  principalId: string;
  qualifier?: string | null;
  users: AccessUser[];
}

interface HeaderAccess {
  planHeaderId: string;
  name: string;
  ownerOrgUnitId: string;
  ownerOrgUnitName?: string | null;
  status: string;
  assignedTo?: PrincipalSlot | null;
  owner?: PrincipalSlot | null;
  warnings: string[];
}

interface EntryAccessAnalysis {
  planVersionId: string;
  headers: HeaderAccess[];
}

/**
 * Display names for the org nodes the principal slots point at. Most slots point at a node that is
 * itself some header's Planning Org Unit (the generated convention), so those names ride along in
 * the response; only the remainder (e.g. an owner role on an ancestor without its own header) is
 * fetched — one GET per distinct unknown node, id fallback on failure.
 */
function useOrgUnitNames(analysis: EntryAccessAnalysis | undefined): Record<string, string> {
  const fromHeaders = useMemo(() => {
    const map: Record<string, string> = {};
    for (const h of analysis?.headers ?? []) {
      if (h.ownerOrgUnitName) map[h.ownerOrgUnitId] = h.ownerOrgUnitName;
    }
    return map;
  }, [analysis]);

  const [fetched, setFetched] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!analysis) return;
    const missing = new Set<string>();
    for (const h of analysis.headers) {
      for (const slot of [h.assignedTo, h.owner]) {
        if (slot && !fromHeaders[slot.principalId]) missing.add(slot.principalId);
      }
    }
    if (missing.size === 0) return;
    let cancelled = false;
    Promise.all(
      [...missing].map(async (id) => {
        try {
          const res = await apiClient.get<{ name?: string }>(`/api/core/orgunit/${id}`);
          return [id, res.data?.name || id] as const;
        } catch {
          return [id, id] as const; // keep the id visible rather than hiding the slot
        }
      }),
    ).then((entries) => {
      if (!cancelled) setFetched(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [analysis, fromHeaders]);

  return useMemo(() => ({ ...fetched, ...fromHeaders }), [fetched, fromHeaders]);
}

function UserChips({ users }: { users: AccessUser[] }) {
  const { t } = useTranslation();
  if (users.length === 0) {
    return (
      <Chip
        size="small"
        color="error"
        variant="outlined"
        label={t('Finance.EntryAccess.NoHolders', { defaultValue: 'No current holders' })}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {users.map((u) => (
        <Tooltip key={u.userId} title={u.userId}>
          <Chip size="small" variant="outlined" label={u.userName || `${u.userId.slice(0, 8)}…`} />
        </Tooltip>
      ))}
    </Box>
  );
}

function SlotCell({ slot, nodeName }: { slot?: PrincipalSlot | null; nodeName?: string }) {
  const { t } = useTranslation();
  if (!slot) {
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={t('Finance.EntryAccess.Unassigned', { defaultValue: 'Unassigned' })}
      />
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="body2">
        {slot.qualifier ? `${slot.qualifier} @ ` : ''}
        {nodeName || slot.principalId}
      </Typography>
      <UserChips users={slot.users} />
    </Box>
  );
}

function StatusChip({ status }: { status: string }) {
  const { t } = useTranslation();
  const color = status === 'SUBMITTED' ? 'info' : status === 'RETURNED' ? 'warning' : undefined;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={t(`Enum:SubmissionStatus:${status}`, { defaultValue: status })}
    />
  );
}

export interface PlanVersionEntryAccessDialogProps {
  entityId: string;
  open: boolean;
  onClose: () => void;
}

export function PlanVersionEntryAccessDialog({ entityId, open, onClose }: PlanVersionEntryAccessDialogProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['plan-version-entry-access', entityId],
    queryFn: async () =>
      (await apiClient.get<EntryAccessAnalysis>(`/api/finance/planversion/entry-access-analysis/${entityId}`)).data,
    enabled: open,
  });

  const nodeNames = useOrgUnitNames(data);

  const distinctUserCount = useMemo(() => {
    const ids = new Set<string>();
    for (const h of data?.headers ?? []) {
      for (const slot of [h.assignedTo, h.owner]) {
        for (const u of slot?.users ?? []) ids.add(u.userId);
      }
    }
    return ids.size;
  }, [data]);

  const uncovered = data?.headers.filter((h) => h.warnings.includes('NoEntryAccess')).length ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('Finance.EntryAccess.Title', { defaultValue: 'Explain entry access' })}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('Finance.EntryAccess.Loading', { defaultValue: 'Resolving assignments…' })}
            </Typography>
          </Box>
        )}

        {!!error && (
          <Alert severity="error">
            {t('Finance.EntryAccess.LoadFailed', { defaultValue: 'Failed to load the entry access analysis.' })}
          </Alert>
        )}

        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={t('Finance.EntryAccess.HeaderCount', { defaultValue: '{{n}} headers', n: data.headers.length })}
              />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={t('Finance.EntryAccess.UsersWithAccess', {
                  defaultValue: '{{n}} users with entry access',
                  n: distinctUserCount,
                })}
              />
              {uncovered > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={t('Finance.EntryAccess.UncoveredHeaders', {
                    defaultValue: '{{n}} headers nobody can enter',
                    n: uncovered,
                  })}
                />
              )}
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Finance.EntryAccess.ColHeader', { defaultValue: 'Header' })}</TableCell>
                  <TableCell>{t('Finance.EntryAccess.ColOrgUnit', { defaultValue: 'Planning org unit' })}</TableCell>
                  <TableCell>{t('Finance.EntryAccess.ColStatus', { defaultValue: 'Status' })}</TableCell>
                  <TableCell>{t('Finance.EntryAccess.ColAssignedTo', { defaultValue: 'Assigned to (contributor)' })}</TableCell>
                  {/* A header is never approved — only the version is. The Owner's power here
                      is to send a submitted header back for rework. */}
                  <TableCell>{t('Finance.EntryAccess.ColOwner', { defaultValue: 'Owner (may return)' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.headers.map((h) => (
                  <TableRow key={h.planHeaderId} sx={{ verticalAlign: 'top' }}>
                    <TableCell>
                      {/* The drill-down into what was actually entered. Entry is per header,
                          so this list is the only place a per-header link is meaningful —
                          a version has many, and a link from the version could not pick one.
                          The grid is read-only while the version is in review, which is
                          exactly what an approver wants. */}
                      <Link
                        component={RouterLink}
                        to={`/finance/plan-entry/${h.planHeaderId}`}
                        variant="body2"
                        underline="hover"
                        onClick={onClose}
                      >
                        {h.name}
                      </Link>
                      {h.warnings.includes('NoEntryAccess') && (
                        <Chip
                          size="small"
                          color="error"
                          label={t('Finance.EntryAccess.NoEntryAccess', { defaultValue: 'No entry access' })}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{h.ownerOrgUnitName || h.ownerOrgUnitId}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={h.status} />
                    </TableCell>
                    <TableCell>
                      <SlotCell slot={h.assignedTo} nodeName={h.assignedTo ? nodeNames[h.assignedTo.principalId] : undefined} />
                    </TableCell>
                    <TableCell>
                      <SlotCell slot={h.owner} nodeName={h.owner ? nodeNames[h.owner.principalId] : undefined} />
                    </TableCell>
                  </TableRow>
                ))}
                {data.headers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        {t('Finance.EntryAccess.Empty', { defaultValue: 'This version has no plan headers yet.' })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Alert severity="info">
              {t('Finance.EntryAccess.Footer', {
                defaultValue:
                  'Listed users see and may enter their headers; owners may additionally return submitted ones. ' +
                  'Users holding the ViewSubtree / ViewAll permissions see headers read-only and are not listed here. ' +
                  'A SUBMITTED header is read-only for everyone until returned. ' +
                  'Membership changes take effect within about two minutes.',
              })}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
      </DialogActions>
    </Dialog>
  );
}
